/**
 * Глобальный контекст auth: текущий user, login, logout.
 *
 * fastapi-users возвращает httpOnly-cookie kb_session — её ставит браузер,
 * мы не имеем к ней доступа из JS. Поэтому single source of truth — это
 * успех/провал GET /api/users/me. На mount делаем этот запрос; пока идёт —
 * isLoading=true; после — либо user, либо null.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AUTH_EXPIRED_EVENT, ApiError, api } from "@/api/client";

export const AUTH_RETURN_TO_KEY = "kb:auth:return-to";
export const AUTH_EXPIRED_NOTICE_KEY = "kb:auth:expired-notice";

/** Разрешаем возврат после login только на внутренний путь приложения. */
export function safeAuthReturnTo(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const path = value.trim();
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  if (path === "/login" || path.startsWith("/login?") || path.startsWith("/login#")) return null;
  return path;
}

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  display_name: string | null;
  avatar_filename?: string | null;
  role?: string;
  position?: string | null;
  bio?: string | null;
  last_seen_at?: string | null;
  /** Список департаментов: 'sales' | 'production' | 'admin' | 'supply'. */
  departments?: string[];
  can_access_finance?: boolean;
  can_access_messenger?: boolean;
  can_access_settings?: boolean;
  can_delete_production?: boolean;
  can_use_calls_ai?: boolean;
  can_use_assistant?: boolean;
  can_use_violations_ai?: boolean;
}

/** Доступ к разделам: учитывает is_superuser, role (owner/admin),
 *  departments, can_access_finance/messenger флаги. */
export function hasAccess(
  user: User | null,
  area: "sales" | "supply" | "finance" | "messenger" | "settings" | "production" | "production-admin" | "delete-production" | "calls-ai" | "violations-ai" | "ai-admin" | "marketing",
): boolean {
  if (!user) return false;
  if (user.is_superuser) return true;
  const role = user.role ?? "";
  if (area === "production-admin" || area === "ai-admin") {
    return role === "owner" || role === "admin";
  }
  if (area === "delete-production") {
    if (role === "owner" || role === "admin") return true;
    return !!user.can_delete_production;
  }
  if (area === "calls-ai") {
    // AI-агент по звонкам — superuser/owner/admin/assistant + явный грант.
    if (role === "owner" || role === "admin" || role === "assistant") return true;
    return !!user.can_use_calls_ai;
  }
  if (area === "violations-ai") {
    if (role === "owner" || role === "admin" || role === "assistant") return true;
    return !!user.can_use_violations_ai;
  }
  if (role === "owner" || role === "admin") return true;
  const depts = user.departments ?? [];
  if (area === "sales") return depts.includes("sales");
  if (area === "supply") return depts.includes("supply");
  if (area === "marketing") return depts.includes("marketing");
  if (area === "finance") return !!user.can_access_finance || role === "accountant";
  if (area === "messenger") return user.can_access_messenger !== false;
  if (area === "settings") return !!user.can_access_settings;
  if (area === "production") return depts.includes("production");
  return false;
}

export interface LoginResult {
  mfaRequired: boolean;
  challengeToken?: string;
  expiresIn?: number;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function clearPrivateBrowserCaches(): Promise<void> {
  if (typeof window === "undefined" || !("caches" in window)) return;
  try {
    const names = await window.caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith("kb-chat-images-") || name.startsWith("kb-avatars-"))
        .map((name) => window.caches.delete(name)),
    );
  } catch { /* Cache Storage can be unavailable in restricted webviews. */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const refresh = useCallback(async () => {
    try {
      const me = await api<User>("/api/users/me");
      if (userRef.current && userRef.current.id !== me.id) {
        queryClient.clear();
        void clearPrivateBrowserCaches();
      }
      setUser(me);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // 401 — пользователь не авторизован, разлогиниваем
        queryClient.clear();
        void clearPrivateBrowserCaches();
        setUser(null);
      } else {
        // Сетевая ошибка или другой API error — НЕ разлогиниваем
        // Оставляем prevUser как есть (может быть null или предыдущее значение)
        console.warn("auth/me failed", e);
        // НЕ вызываем setUser(null) — ждём когда сетевое соединение восстановится
      }
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    const onAuthExpired = () => {
      try {
        const returnTo = safeAuthReturnTo(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        if (returnTo) sessionStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);
        // На первом открытии без cookie это обычный экран входа, не «истёкшая»
        // сессия. Уведомление показываем только если пользователь уже был внутри.
        if (userRef.current) sessionStorage.setItem(AUTH_EXPIRED_NOTICE_KEY, "1");
      } catch { /* sessionStorage может быть запрещён политикой браузера */ }
      queryClient.clear();
      void clearPrivateBrowserCaches();
      setUser(null);
      setIsLoading(false);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, [queryClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // TWA/PWA может прожить в фоне дольше cookie/JWT. Проверяем сессию при
  // возврате приложения, восстановлении сети и периодически в активном окне.
  useEffect(() => {
    const refreshWhenUsable = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        void refresh();
      }
    };
    const interval = window.setInterval(refreshWhenUsable, 5 * 60 * 1000);
    window.addEventListener("online", refreshWhenUsable);
    window.addEventListener("pageshow", refreshWhenUsable);
    document.addEventListener("visibilitychange", refreshWhenUsable);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", refreshWhenUsable);
      window.removeEventListener("pageshow", refreshWhenUsable);
      document.removeEventListener("visibilitychange", refreshWhenUsable);
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const body = new URLSearchParams();
      body.set("username", email);
      body.set("password", password);
      // raw: true — чтобы получить Response напрямую (cookie set нашим браузером
      // автоматически из Set-Cookie). НО: raw также пропускает проверку res.ok,
      // поэтому делаем её здесь вручную, иначе 400 LOGIN_BAD_CREDENTIALS
      // молча проглотится и не дойдёт до catch в LoginPage.
      const res = (await api("/api/auth/cookie/login", {
        method: "POST",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        raw: true,
      })) as unknown as Response;
      if (!res.ok) {
        let err: unknown = null;
        try { err = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, err);
      }
      if (res.status === 202) {
        const challenge = (await res.json()) as {
          mfa_required: boolean;
          challenge_token: string;
          expires_in: number;
        };
        if (!challenge.mfa_required || !challenge.challenge_token) {
          throw new ApiError(500, { detail: "Invalid MFA challenge response" });
        }
        return {
          mfaRequired: true,
          challengeToken: challenge.challenge_token,
          expiresIn: challenge.expires_in,
        };
      }
      await refresh();
      return { mfaRequired: false };
    },
    [refresh],
  );

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string) => {
      const res = (await api("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ challenge_token: challengeToken, code }),
        raw: true,
      })) as unknown as Response;
      if (!res.ok) {
        let err: unknown = null;
        try { err = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, err);
      }
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/cookie/logout", { method: "POST", raw: true });
    } catch (e) {
      console.warn("logout failed", e);
    }
    try {
      sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
      sessionStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
    } catch { /* ignore */ }
    queryClient.clear();
    await clearPrivateBrowserCaches();
    setUser(null);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, login, verifyMfa, logout, refresh }),
    [user, isLoading, login, verifyMfa, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
